import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { isIterableArray } from 'helpers/utils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Flex from './Flex';
import classNames from 'classnames';
import avatarImg from 'assets/img/team/avatar.png';

import UploadFiles from 'components/common/UploadFiles';
import { Badge, Button } from 'react-bootstrap';

const IconUpload = ({ propUploader: { iconClass, ...rest } }) => {
 return (
  <UploadFiles
   {...rest}
   accept="image/*"
   dropzoneArea={false}
   placeholder={
    <Button
     variant="falcon-default"
     type="button"
     className={'icon-item icon-item-sm position-absolute bottom-0 end-0 z-index-2 ' + iconClass}
    >
     <FontAwesomeIcon icon="pencil-alt" />
    </Button>
   }
  />
 );
};

const Avatar = ({
 size,
 rounded,
 src,
 obj,
 emoji,
 name,
 className,
 mediaClass,
 isExact,
 icon,
 uploader,
 propUploader,
 style
}) => {
 const [imgValid, setImgValid] = useState(isIterableArray(src));
 const classNames = ['position-relative', 'avatar', `avatar-${size}`, 'rounded-circle', className].join(' ');
 const mediaClasses = [rounded ? `rounded-${rounded}` : 'rounded', mediaClass].join(' ');

 const getAvatar = () => {
  if (isIterableArray(src) && src.length > 1) {
   return (
    <div className={`${mediaClasses} overflow-hidden h-100 d-flex`}>
     <div className={`w-50 border-right ${src.length > 3 && 'd-flex flex-column'}`}>
      {src.length > 3 ? (
       src.map((e, i) => {
        if (i < 2) return <img key={e.path} src={e.base64} alt="" className="h-50 border-bottom" />;
       })
      ) : (
       <img src={src[0].base64} alt="" />
      )}
     </div>
     <div className="w-50 d-flex flex-column">
      {src.length > 0 &&
       src.map((e, i) => {
        const index = src.length > 3 ? 3 : 1;
        if (i >= index && i) return <img key={e.path} src={e.base64} alt="" className="h-50 border-bottom" />;
       })}
     </div>
    </div>
   );
  } else if (isIterableArray(src)) return <img className={mediaClasses} src={src[0].base64} alt="" />;

  if (src) {
   const img = new Image();
   img.src = src;
   img.onload = () => setImgValid(true);
   img.onerror = () => setImgValid(false);
  }

  if (src && imgValid) return <img className={mediaClasses} src={src} alt="" />;

  if (obj) {
   const { val, badge, placeClass } = obj;
   return (
    <>
     <div className={`avatar-name ${mediaClasses}`}>
      <span>{val}</span>
     </div>
     {badge && (
      <div className={placeClass}>
       <Badge pill bg="secondary" className="p-1">
        {badge}
       </Badge>
      </div>
     )}
    </>
   );
  }

  if (name) {
   return (
    <div className={`avatar-name ${mediaClasses}`}>
     <span>{isExact ? name : name.match(/\b\w/g).slice(0, 2).join('')}</span>
    </div>
   );
  }

  if (icon) {
   return (
    <Flex className={`avatar-name ${mediaClasses}`}>
     <FontAwesomeIcon icon={icon} />
    </Flex>
   );
  }

  if (emoji) {
   return (
    <div className={`avatar-emoji ${mediaClasses}`}>
     <span role="img" aria-label="Emoji">
      {emoji}
     </span>
    </div>
   );
  }

  return <img className={mediaClasses} src={avatarImg} alt="" />;
 };

 return (
  <div className={classNames} style={style}>
   {getAvatar()} {uploader && <IconUpload propUploader={propUploader} />}
  </div>
 );
};

export const AvatarGroup = ({ children, dense, className }) => {
 return (
  <div
   className={classNames(className, 'avatar-group', {
    'avatar-group-dense': dense
   })}
  >
   {children}
  </div>
 );
};

Avatar.propTypes = {
 size: PropTypes.oneOf(['s', 'm', 'l', 'xl', '2xl', '3xl', '4xl', '5xl']),
 rounded: PropTypes.string,
 src: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
 name: PropTypes.string,
 obj: PropTypes.object,
 emoji: PropTypes.string,
 className: PropTypes.string,
 mediaClass: PropTypes.string,
 isExact: PropTypes.bool,
 icon: PropTypes.string,
 uploader: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
 propUploader: PropTypes.object,
 style: PropTypes.object
};

Avatar.defaultProps = {
 size: 'xl',
 rounded: 'circle',
 isExact: false,
 uploader: false,
 style: {}
};

AvatarGroup.propTypes = {
 children: PropTypes.node.isRequired,
 className: PropTypes.string,
 dense: PropTypes.bool
};

IconUpload.propTypes = { propUploader: PropTypes.object };

export default Avatar;
